import React from "react";
import { ArrowRight } from "lucide-react";
import type { CategoryDetails, SubCategory } from "../../../types";

export type ServicesRowLinkComponent = React.ComponentType<{
  href: string;
  children?: React.ReactNode;
  className?: string;
  title?: string;
  style?: React.CSSProperties;
  [key: string]: any;
}>;

export type ServicesRowImage = {
  src?: string;
  url?: string;
  original?: string;
  thumbnail?: string;
  alt?: string;
  title?: string;
  originalAlt?: string;
  thumbnailAlt?: string;
};

export type ServicesRowTileCta = {
  label?: string;
  href?: string;
  ariaLabel?: string;
};

type ServicesRowSubCategoryFields = Omit<
  Partial<SubCategory>,
  | "id"
  | "title"
  | "name"
  | "description"
  | "link"
  | "images"
  | "heroImage"
  | "accentColor"
  | "accentDarkColor"
>;

export type ServicesRowTile = ServicesRowSubCategoryFields & {
  id: string | number;
  title?: string;
  name?: string;
  overline?: string;
  text?: string;
  description?: string;
  href?: string;
  link?: string;
  image?: ServicesRowImage;
  images?: ServicesRowImage[];
  heroImage?: ServicesRowImage;
  imageUrl?: string;
  imageAlt?: string;
  icon?: React.ReactNode;
  iconSrc?: string;
  iconAlt?: string;
  accentColor?: string;
  accentDarkColor?: string;
  cta?: ServicesRowTileCta;
  ctaLabel?: string;
  ctaHref?: string;
  ctaAriaLabel?: string;
};

export type ServicesRowBottomCta = {
  label: string;
  href: string;
  ariaLabel?: string;
};

export type ServicesRowProps = {
  items?: ServicesRowTile[];
  service?: CategoryDetails;
  Link: ServicesRowLinkComponent;
  overline?: string;
  title?: string;
  text?: string;
  description?: string;
  tileCtaLabel?: string;
  bottomCta?: ServicesRowBottomCta;
  bottomCtaText?: string;
  bottomCtaHref?: string;
  bottomCtaAriaLabel?: string;
  accentColor?: string;
  backgroundColor?: string;
  maxWidth?: number | string;
  visibleTileCount?: number;
  className?: string;
  ariaLabel?: string;
};

const defaultAccentColor = "#ed1c24";

function getImage(item: ServicesRowTile): ServicesRowImage {
  if (item.image) return item.image;

  const heroImage = item.heroImage as ServicesRowImage | undefined;
  if (heroImage) return heroImage;

  const galleryImage = item.images?.[0] as ServicesRowImage | undefined;
  if (galleryImage) return galleryImage;

  if (item.imageUrl) {
    return {
      src: item.imageUrl,
      alt: item.imageAlt || item.title || item.name,
    };
  }

  return {};
}

function getImageSrc(image: ServicesRowImage): string {
  return image.thumbnail || image.src || image.url || image.original || "";
}

function getImageAlt(image: ServicesRowImage, title: string): string {
  return image.alt || image.thumbnailAlt || image.originalAlt || image.title || title;
}

function getItemTitle(item: ServicesRowTile): string {
  return item.title || item.name || "Service";
}

function getItemText(item: ServicesRowTile): string | undefined {
  return item.text || item.description;
}

function getItemHref(item: ServicesRowTile): string {
  return item.cta?.href || item.ctaHref || item.href || item.link || "#";
}

function getItemCtaLabel(item: ServicesRowTile, fallback: string): string {
  return item.cta?.label || item.ctaLabel || fallback;
}

function getSectionText(service?: CategoryDetails, description?: string, text?: string) {
  if (text) return text;
  if (description) return description;
  if (Array.isArray(service?.description)) return service?.description?.join(" ");
  return undefined;
}

function resolveBottomCta({
  bottomCta,
  bottomCtaText,
  bottomCtaHref,
  bottomCtaAriaLabel,
}: Pick<
  ServicesRowProps,
  "bottomCta" | "bottomCtaText" | "bottomCtaHref" | "bottomCtaAriaLabel"
>) {
  if (bottomCta) return bottomCta;
  if (!bottomCtaText || !bottomCtaHref) return undefined;

  return {
    label: bottomCtaText,
    href: bottomCtaHref,
    ariaLabel: bottomCtaAriaLabel,
  };
}

function ServicesRowIcon({ item }: { item: ServicesRowTile }) {
  if (item.iconSrc) {
    return <img src={item.iconSrc} alt={item.iconAlt || ""} className="services-row-tile-icon-image" />;
  }

  if (item.icon) {
    return <span className="services-row-tile-icon-node">{item.icon}</span>;
  }

  return null;
}

const servicesRowCss = `
.services-row {
  --services-row-accent: ${defaultAccentColor};
  --services-row-bg: #ffffff;
  --services-row-text: #101828;
  --services-row-muted: #475467;
  --services-row-border: rgba(16, 24, 40, 0.12);
  --services-row-shadow: 0 10px 28px rgba(16, 24, 40, 0.08);
  --services-row-card-width: 178px;
  --services-row-image-height: 142px;
  --services-row-gap: 18px;
  width: 100%;
  background: var(--services-row-bg);
  color: var(--services-row-text);
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  box-sizing: border-box;
}
.services-row,
.services-row * {
  box-sizing: border-box;
}
.services-row-shell {
  width: 100%;
  margin: 0 auto;
  padding: 28px 16px 24px;
}
.services-row-header {
  max-width: 820px;
  margin: 0 auto 26px;
  text-align: center;
}
.services-row-overline {
  margin: 0 0 8px;
  color: var(--services-row-accent);
  font-size: 11px;
  line-height: 1.1;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}
.services-row-title {
  margin: 0;
  font-size: 30px;
  line-height: 1.12;
  font-weight: 800;
  letter-spacing: 0;
  color: var(--services-row-text);
}
.services-row-text {
  max-width: 650px;
  margin: 12px auto 0;
  color: var(--services-row-muted);
  font-size: 15px;
  line-height: 1.65;
}
.services-row-viewport {
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  padding: 28px 2px 12px;
  scroll-behavior: smooth;
  scroll-snap-type: x proximity;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}
.services-row-viewport::-webkit-scrollbar {
  display: none;
}
.services-row-track {
  display: flex;
  align-items: stretch;
  justify-content: flex-start;
  gap: var(--services-row-gap);
  min-width: max-content;
}
.services-row-card-wrap {
  flex: 0 0 var(--services-row-card-width);
  width: var(--services-row-card-width);
  scroll-snap-align: start;
  display: flex;
}
.services-row-card-link {
  display: flex;
  width: 100%;
  min-width: 0;
  color: inherit;
  text-decoration: none;
}
.services-row-card {
  position: relative;
  display: grid;
  grid-template-rows: var(--services-row-image-height) minmax(0, 1fr);
  width: 100%;
  min-height: 292px;
  border: 1px solid var(--services-row-border);
  border-radius: 7px;
  background: #ffffff;
  box-shadow: var(--services-row-shadow);
  transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease;
}
.services-row-card-link:hover .services-row-card,
.services-row-card-link:focus-visible .services-row-card {
  transform: translateY(-2px);
  box-shadow: 0 16px 34px rgba(16, 24, 40, 0.12);
  border-color: color-mix(in srgb, var(--tile-accent, var(--services-row-accent)) 24%, var(--services-row-border));
}
.services-row-image-wrap {
  position: relative;
  width: 100%;
  min-width: 0;
  height: var(--services-row-image-height);
  overflow: hidden;
  border-radius: 7px 7px 0 0;
  background: #f2f4f7;
}
.services-row-image,
.services-row-image-fallback {
  width: 100%;
  height: 100%;
  display: block;
}
.services-row-image {
  object-fit: cover;
  transform: scale(1);
  transition: transform 420ms ease;
}
.services-row-card-link:hover .services-row-image,
.services-row-card-link:focus-visible .services-row-image {
  transform: scale(1.055);
}
.services-row-image-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  color: var(--services-row-muted);
  text-align: center;
  font-size: 13px;
  line-height: 1.35;
  background: linear-gradient(180deg, #f8fafc 0%, #eef2f6 100%);
}
.services-row-icon-badge {
  position: absolute;
  z-index: 2;
  top: -31px;
  left: 50%;
  width: 62px;
  height: 62px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  border: 1px solid rgba(16, 24, 40, 0.08);
  background: #ffffff;
  color: var(--tile-accent, var(--services-row-accent));
  box-shadow: 0 10px 24px rgba(16, 24, 40, 0.14);
  transform: translateX(-50%);
  pointer-events: none;
}
.services-row-tile-icon-node,
.services-row-tile-icon-node svg,
.services-row-tile-icon-image {
  width: 30px;
  height: 30px;
  display: block;
}
.services-row-tile-icon-node svg {
  stroke-width: 2.25;
}
.services-row-tile-icon-image {
  object-fit: contain;
}
.services-row-content {
  display: flex;
  min-height: 0;
  flex-direction: column;
  align-items: center;
  gap: 9px;
  padding: 18px 15px 16px;
  text-align: center;
}
.services-row-tile-overline {
  margin: 0 0 -3px;
  color: var(--tile-accent, var(--services-row-accent));
  font-size: 10px;
  line-height: 1.2;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}
.services-row-tile-title {
  margin: 0;
  color: var(--services-row-text);
  font-size: 15px;
  line-height: 1.22;
  font-weight: 800;
  letter-spacing: 0;
}
.services-row-tile-text {
  margin: 0;
  color: #344054;
  font-size: 12px;
  line-height: 1.55;
}
.services-row-tile-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  margin-top: auto;
  padding-top: 10px;
  color: var(--tile-accent, var(--services-row-accent));
  font-size: 12px;
  line-height: 1;
  font-weight: 800;
  white-space: nowrap;
}
.services-row-tile-cta svg {
  flex: 0 0 auto;
  transition: transform 180ms ease;
}
.services-row-card-link:hover .services-row-tile-cta svg,
.services-row-card-link:focus-visible .services-row-tile-cta svg {
  transform: translateX(3px);
}
.services-row-controls {
  display: none;
  align-items: center;
  justify-content: center;
  gap: 14px;
  margin: 12px auto 0;
}
.services-row[data-services-row-overflow="true"] .services-row-controls {
  display: flex;
}
.services-row-scroll-button {
  width: 42px;
  height: 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(16, 24, 40, 0.12);
  border-radius: 999px;
  background: #ffffff;
  color: var(--services-row-text);
  box-shadow: 0 8px 20px rgba(16, 24, 40, 0.10);
  cursor: pointer;
  transition: background 160ms ease, color 160ms ease, transform 160ms ease, opacity 160ms ease;
}
.services-row-scroll-button:hover {
  background: var(--services-row-accent);
  color: #ffffff;
  transform: translateY(-1px);
}
.services-row-scroll-button:disabled {
  cursor: default;
  opacity: 0.36;
  transform: none;
  background: #ffffff;
  color: var(--services-row-muted);
}
.services-row-indicator {
  position: relative;
  width: min(180px, 38vw);
  height: 5px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(16, 24, 40, 0.10);
}
.services-row-indicator-bar {
  position: absolute;
  inset: 0 auto 0 0;
  left: var(--services-row-progress-x, 0);
  width: var(--services-row-progress-width, 34%);
  border-radius: inherit;
  background: var(--services-row-accent);
  transition: width 160ms ease, left 160ms ease;
}
.services-row-bottom-cta-wrap {
  display: flex;
  justify-content: center;
  margin-top: 22px;
}
.services-row-bottom-cta {
  display: inline-flex;
  min-width: 184px;
  min-height: 44px;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border-radius: 4px;
  background: var(--services-row-accent);
  color: #ffffff;
  padding: 12px 22px;
  font-size: 14px;
  line-height: 1;
  font-weight: 800;
  text-decoration: none;
  box-shadow: 0 10px 22px color-mix(in srgb, var(--services-row-accent) 24%, transparent);
  transition: transform 160ms ease, box-shadow 160ms ease, filter 160ms ease;
}
.services-row-bottom-cta:hover,
.services-row-bottom-cta:focus-visible {
  transform: translateY(-1px);
  filter: brightness(0.96);
}
@media (min-width: 768px) {
  .services-row-track {
    justify-content: center;
  }
  .services-row[data-services-row-overflow="true"] .services-row-track {
    justify-content: flex-start;
  }
}
@media (max-width: 767px) {
  .services-row-shell {
    padding: 26px 14px 20px;
  }
  .services-row-header {
    max-width: 310px;
    margin-bottom: 18px;
  }
  .services-row-title {
    font-size: 22px;
    line-height: 1.12;
  }
  .services-row-text {
    font-size: 13px;
    line-height: 1.55;
  }
  .services-row-viewport {
    overflow: visible;
    padding: 0;
    scroll-snap-type: none;
  }
  .services-row-track {
    display: grid;
    min-width: 0;
    gap: 12px;
  }
  .services-row-card-wrap {
    width: 100%;
    min-width: 0;
    flex-basis: auto;
  }
  .services-row-card {
    min-height: 150px;
    grid-template-columns: minmax(118px, 42%) minmax(0, 1fr);
    grid-template-rows: minmax(150px, auto);
  }
  .services-row-image-wrap {
    height: 100%;
    min-height: 150px;
    border-radius: 7px 0 0 7px;
  }
  .services-row-icon-badge {
    top: 50%;
    left: 42%;
    width: 44px;
    height: 44px;
    color: #ffffff;
    background: var(--tile-accent, var(--services-row-accent));
    border: 2px solid #ffffff;
    box-shadow: 0 8px 20px rgba(16, 24, 40, 0.18);
  }
  .services-row-tile-icon-node,
  .services-row-tile-icon-node svg,
  .services-row-tile-icon-image {
    width: 22px;
    height: 22px;
  }
  .services-row-content {
    align-items: flex-start;
    justify-content: center;
    gap: 8px;
    padding: 16px 14px 14px 18px;
    text-align: left;
  }
  .services-row-tile-title {
    font-size: 14px;
    line-height: 1.25;
  }
  .services-row-tile-text {
    font-size: 11px;
    line-height: 1.48;
  }
  .services-row-controls {
    display: none !important;
  }
  .services-row-bottom-cta-wrap {
    margin-top: 16px;
  }
  .services-row-bottom-cta {
    width: min(100%, 284px);
  }
}
`;

function getServicesRowControlsHtml(viewportId: string) {
  const runScroll = [
    "(function(button,direction){",
    "var root=button.closest('[data-services-row]');if(!root)return;",
    "var viewport=root.querySelector('[data-services-row-viewport]');",
    "var track=root.querySelector('[data-services-row-track]');if(!viewport||!track)return;",
    "var styles=window.getComputedStyle(track);",
    "var gap=parseFloat(styles.columnGap||styles.gap||'0')||0;",
    "var card=track.querySelector('[data-services-row-card]');",
    "var step=card?card.getBoundingClientRect().width+gap:viewport.clientWidth*.85;",
    "var update=function(){",
    "var max=Math.max(0,viewport.scrollWidth-viewport.clientWidth);",
    "var overflow=viewport.scrollWidth>viewport.clientWidth+2;",
    "root.setAttribute('data-services-row-overflow',overflow?'true':'false');",
    "var bar=root.querySelector('[data-services-row-indicator-bar]');",
    "if(bar){",
    "var visibleRatio=overflow?Math.max(.16,Math.min(1,viewport.clientWidth/viewport.scrollWidth)):1;",
    "var progress=max>0?Math.max(0,Math.min(1,viewport.scrollLeft/max)):0;",
    "var width=visibleRatio*100;var x=progress*(100-width);",
    "bar.style.setProperty('--services-row-progress-width',width+'%');",
    "bar.style.setProperty('--services-row-progress-x',x+'%');",
    "}};",
    "viewport.scrollBy({left:direction*step,behavior:'smooth'});update();",
    "var ticks=0;var timer=window.setInterval(function(){",
    "update();ticks+=1;if(ticks>8)window.clearInterval(timer);",
    "},80);",
    "})(this,",
  ].join("");

  return `
    <button
      type="button"
      class="services-row-scroll-button"
      data-services-row-prev
      aria-controls="${viewportId}"
      aria-label="Scroll services back"
      onclick="${runScroll}-1)"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
        <path d="m15 18-6-6 6-6" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"></path>
      </svg>
    </button>
    <div class="services-row-indicator" data-services-row-indicator aria-hidden="true">
      <span class="services-row-indicator-bar" data-services-row-indicator-bar></span>
    </div>
    <button
      type="button"
      class="services-row-scroll-button"
      data-services-row-next
      aria-controls="${viewportId}"
      aria-label="Scroll services forward"
      onclick="${runScroll}1)"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
        <path d="m9 18 6-6-6-6" stroke="currentColor" stroke-width="2.3" stroke-linecap="round" stroke-linejoin="round"></path>
      </svg>
    </button>
  `;
}

export default function ServicesRow({
  items,
  service,
  Link,
  overline = "Our Services",
  title,
  text,
  description,
  tileCtaLabel = "Learn More",
  bottomCta,
  bottomCtaText,
  bottomCtaHref,
  bottomCtaAriaLabel,
  accentColor = defaultAccentColor,
  backgroundColor = "#ffffff",
  maxWidth = 1180,
  visibleTileCount = 6,
  className = "",
  ariaLabel,
}: ServicesRowProps) {
  const rowId = React.useId().replace(/:/g, "");
  const resolvedItems = (items?.length ? items : service?.subCategories || []) as ServicesRowTile[];
  const resolvedTitle = title || service?.long_name || service?.name;
  const resolvedText = getSectionText(service, description, text);
  const resolvedBottomCta = resolveBottomCta({
    bottomCta,
    bottomCtaText,
    bottomCtaHref,
    bottomCtaAriaLabel,
  });
  const shouldRenderControls = resolvedItems.length > visibleTileCount;

  if (!resolvedItems.length) return null;

  return (
    <section
      className={`services-row ${className}`}
      data-services-row
      data-services-row-overflow={shouldRenderControls ? "true" : "false"}
      aria-label={ariaLabel || resolvedTitle || overline}
      style={
        {
          "--services-row-accent": accentColor,
          "--services-row-bg": backgroundColor,
        } as React.CSSProperties
      }
    >
      <style>{servicesRowCss}</style>

      <div className="services-row-shell" style={{ maxWidth }}>
        {(overline || resolvedTitle || resolvedText) ? (
          <div className="services-row-header">
            {overline ? <p className="services-row-overline">{overline}</p> : null}
            {resolvedTitle ? <h2 className="services-row-title">{resolvedTitle}</h2> : null}
            {resolvedText ? <p className="services-row-text">{resolvedText}</p> : null}
          </div>
        ) : null}

        <div
          id={`${rowId}-viewport`}
          className="services-row-viewport"
          data-services-row-viewport
          aria-label="Scrollable services"
          tabIndex={0}
        >
          <div className="services-row-track" data-services-row-track>
            {resolvedItems.map((item) => {
              const itemTitle = getItemTitle(item);
              const itemText = getItemText(item);
              const image = getImage(item);
              const imageSrc = getImageSrc(image);
              const href = getItemHref(item);
              const ctaLabel = getItemCtaLabel(item, tileCtaLabel);
              const itemAccent = item.accentColor || item.accentDarkColor || accentColor;
              const hasIcon = Boolean(item.icon || item.iconSrc);

              return (
                <div
                  key={item.id}
                  className="services-row-card-wrap"
                  data-services-row-card
                  style={{ "--tile-accent": itemAccent } as React.CSSProperties}
                >
                  <Link
                    href={href}
                    title={itemTitle}
                    aria-label={item.cta?.ariaLabel || item.ctaAriaLabel || `${ctaLabel}: ${itemTitle}`}
                    className="services-row-card-link"
                  >
                    <article className="services-row-card">
                      {hasIcon ? (
                        <span className="services-row-icon-badge" aria-hidden="true">
                          <ServicesRowIcon item={item} />
                        </span>
                      ) : null}

                      <div className="services-row-image-wrap">
                        {imageSrc ? (
                          <img
                            src={imageSrc}
                            alt={getImageAlt(image, itemTitle)}
                            className="services-row-image"
                            loading="lazy"
                          />
                        ) : (
                          <div className="services-row-image-fallback">{itemTitle}</div>
                        )}
                      </div>

                      <div className="services-row-content">
                        {item.overline ? <p className="services-row-tile-overline">{item.overline}</p> : null}
                        <h3 className="services-row-tile-title">{itemTitle}</h3>
                        {itemText ? <p className="services-row-tile-text">{itemText}</p> : null}
                        <span className="services-row-tile-cta">
                          {ctaLabel}
                          <ArrowRight size={14} strokeWidth={2.4} aria-hidden="true" />
                        </span>
                      </div>
                    </article>
                  </Link>
                </div>
              );
            })}
          </div>
        </div>

        {shouldRenderControls ? (
          <div
            className="services-row-controls"
            aria-label="Services row controls"
            dangerouslySetInnerHTML={{ __html: getServicesRowControlsHtml(`${rowId}-viewport`) }}
          />
        ) : null}

        {resolvedBottomCta ? (
          <div className="services-row-bottom-cta-wrap">
            <Link
              href={resolvedBottomCta.href}
              aria-label={resolvedBottomCta.ariaLabel}
              className="services-row-bottom-cta"
            >
              {resolvedBottomCta.label}
            </Link>
          </div>
        ) : null}
      </div>

    </section>
  );
}
